/**
 * What Contacten shows per column of the register (spec-63 E2, design §4.2). The register decides
 * which columns exist, in which order and which ones sort; this file only knows how to draw a
 * cell of a `ContactRow`. A column of the register that is not in the map is not shown.
 */
import { LabelChipList } from './components/LabelChip'
import { ContactNameCell, ContactOrganisationCell } from './ContactCells'
import { CategoryCell, DayCell, Dash, OwnerCell, PersonName, PlainText, RelativeTime } from './list/commonCells'
import { LastActivityCell } from './list/LastActivityCell'
import { LastContactCell } from './list/LastContactCell'
import { NextActionCell } from './list/NextActionCell'
import { RhythmCell } from './list/RhythmCell'
import { CONSENT_LABELS, labelOf, SOURCE_LABELS, STATUS_LABELS } from './list/optionLabels'
import type { ColumnRender } from './list/RecordListPage'
import type { ContactRow } from './query/types'

const COLUMNS: Record<string, ColumnRender<ContactRow>> = {
  name: { header: 'Naam', cell: (row) => <ContactNameCell row={row} /> },
  email: { header: 'E-mail', cell: (row) => <PlainText value={row.email} /> },
  phone: { header: 'Telefoon', cell: (row) => <PlainText value={row.phone} /> },
  organisation: { header: 'Organisatie', cell: (row) => <ContactOrganisationCell row={row} /> },
  organisation_city: { header: 'Plaats organisatie', cell: (row) => <PlainText value={row.primary_organisation?.city} /> },
  category: { header: 'Categorie', cell: (row) => <CategoryCell category={row.category} /> },
  labels: { header: 'Labels', cell: (row) => <LabelChipList labels={row.labels} /> },
  owner: { header: 'Eigenaar', cell: (row) => <OwnerCell owner={row.owner} /> },
  status: { header: 'Status', cell: (row) => <PlainText value={labelOf(STATUS_LABELS, row.status)} /> },
  relation_since: { header: 'Relatie sinds', cell: (row) => <DayCell day={row.relation_since} /> },
  introduced_by: { header: 'Geïntroduceerd door', cell: (row) => (row.introduced_by ? <PersonName name={row.introduced_by.full_name} /> : <Dash />) },
  source: { header: 'Bron', cell: (row) => <PlainText value={labelOf(SOURCE_LABELS, row.source)} /> },
  last_contact_at: { header: 'Laatste contact', cell: (row) => <LastContactCell lastContactAt={row.last_contact_at} /> },
  // The cell collapses into a narrow column and needs a width of its own to show who and via whom.
  last_activity_at: { header: 'Laatste activiteit', className: 'min-w-60', cell: (row) => <LastActivityCell activity={row.last_activity} /> },
  next_action_on: { header: 'Volgende actie', cell: (row) => <NextActionCell action={row.next_action} /> },
  open_tasks: { header: 'Open taken', cell: (row) => <PlainText value={row.open_tasks} /> },
  agreements_active: { header: 'Afspraken', cell: (row) => <PlainText value={row.agreements_active || null} /> },
  agreement_next_due: { header: 'Volgende afspraakdeadline', cell: (row) => <DayCell day={row.agreement_next_due} /> },
  rhythm_status: { header: 'Contactritme', cell: (row) => <RhythmCell status={row.rhythm_status} /> },
  newsletter: { header: 'Nieuwsbrief (AVG)', cell: (row) => <PlainText value={labelOf(CONSENT_LABELS, row.newsletter)} /> },
  events_consent: { header: 'Events (AVG)', cell: (row) => <PlainText value={labelOf(CONSENT_LABELS, row.events_consent)} /> },
  on_platform: { header: 'Op het platform', cell: (row) => <PlainText value={row.on_platform ? 'ja' : 'nee'} /> },
  created_at: { header: 'Aangemaakt', cell: (row) => <RelativeTime iso={row.created_at} /> },
  created_by: { header: 'Aangemaakt door', cell: (row) => (row.created_by ? <PersonName name={row.created_by.full_name} /> : <Dash />) },
  updated_at: { header: 'Rij gewijzigd', cell: (row) => <RelativeTime iso={row.updated_at} /> },
}

export function contactColumns(): Record<string, ColumnRender<ContactRow>> {
  return COLUMNS
}
